import { say } from '@/james-playground/aa-family-claim/claim/asks';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import { SUGGEST } from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  ChoiceCards,
  Field,
  fieldLabel,
  isExclusive,
  optionsOf,
  optionText,
  Part,
  Parts,
  questionOf,
  SuggestField,
  useDraft,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  list,
  str,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/**
 * A pick from the form's own list: one answer as radio cards, several as
 * checkable cards (with "None" or "Neither" standing alone), or a short
 * answer with the common ones as chips. "Other" opens the form's follow-up
 * box under the choice that asks for it.
 */
const ChoiceCard = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const { draft, set, visible, values } = useDraft(ask, facts);
  const qid = useQids();
  const ids = ask.entries.filter(visible);
  // One question is already asked in the conversation; several each need theirs.
  const many = ids.length > 1;

  const answered = (id: string) =>
    entry(id).answerType === 'multi_choice'
      ? list(draft[id]).length > 0
      : str(draft[id]).trim() !== '';
  const ready = ids
    .filter((id) => entry(id).requiredness !== 'optional')
    .every(answered);

  const shown = (id: string) => {
    const value = draft[id];
    if (Array.isArray(value))
      return list(value)
        .map((l) => optionText(id, l, v))
        .join(', ');
    return optionText(id, str(value).trim(), v);
  };

  const control = (id: string) => {
    const e = entry(id);
    const labelled = many
      ? { labelledBy: qid(id) }
      : { label: say(ask.prompt, v) };
    if (e.answerType === 'single_choice' || e.answerType === 'multi_choice') {
      const multi = e.answerType === 'multi_choice';
      const picked = multi
        ? list(draft[id])
        : str(draft[id])
          ? [str(draft[id])]
          : [];
      return (
        <ChoiceCards
          {...labelled}
          multi={multi}
          options={optionsOf(id).map((o) => ({
            value: o,
            label: optionText(id, o, v),
          }))}
          selected={picked}
          onPick={(o) => {
            if (!multi) return set(id, o);
            if (isExclusive(o)) return set(id, picked.includes(o) ? [] : [o]);
            const rest = picked.filter((p) => !isExclusive(p));
            set(
              id,
              rest.includes(o) ? rest.filter((p) => p !== o) : [...rest, o],
            );
          }}
        />
      );
    }
    const suggest = SUGGEST[id];
    if (suggest)
      return (
        <SuggestField
          labelledBy={many ? qid(id) : undefined}
          label={many ? undefined : say(ask.prompt, v)}
          chips={suggest.chips}
          other={suggest.other}
          field={say(suggest.field, v)}
          value={str(draft[id])}
          onChange={(value) => set(id, value)}
        />
      );
    return (
      <Field
        label={fieldLabel(id, v)}
        value={str(draft[id])}
        onChange={(ev) => set(id, ev.target.value)}
      />
    );
  };

  // The "Other, tell us where" box sits with the choice it follows.
  const followsOn = (id: string) =>
    /Other/.test(id) && ids.includes(id.replace('Other', ''));

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={ready}
      onSkip={onSkip}
      onContinue={() =>
        onSubmit(values(ids), ids.filter(answered).map(shown).join(' · '))
      }
    >
      <Parts>
        {ids
          .filter((id) => !followsOn(id))
          .map((id) => {
            const follow = ids.find(
              (other) => followsOn(other) && other.replace('Other', '') === id,
            );
            const e = entry(id);
            const typed =
              e.answerType !== 'single_choice' &&
              e.answerType !== 'multi_choice' &&
              !SUGGEST[id];
            return (
              <Part
                key={id}
                qid={qid(id)}
                question={many && !typed ? questionOf(id, v) : undefined}
              >
                {control(id)}
                {follow && control(follow)}
              </Part>
            );
          })}
      </Parts>
    </CardShell>
  );
};

ChoiceCard.displayName = 'ChoiceCard';
export default ChoiceCard;
